'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useId } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import type { FieldShellSize } from '@/components/ui/field-shell';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/ui/utils';
export interface CheckboxFieldProps {
  label: ReactNode;
  description?: string | undefined;
  error?: string | undefined;
  size?: FieldShellSize | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
  name?: string | undefined;
  checked: boolean | 'indeterminate';
  onCheckedChange: (checked: boolean) => void;
  containerClassName?: string | undefined;
  labelClassName?: string | undefined;
  descriptionClassName?: string | undefined;
  errorClassName?: string | undefined;
  /** Forwarded to the underlying checkbox. */
  autoComplete?: string | undefined;
}
function isNonEmptyString(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}
export function CheckboxField(t0) {
  const $ = _c(67);
  const {
    label,
    description,
    error,
    required,
    disabled,
    id,
    name,
    checked,
    onCheckedChange,
    containerClassName,
    labelClassName,
    descriptionClassName,
    errorClassName,
    autoComplete
  } = t0;
  const generatedId = useId();
  let t1;
  if ($[0] !== generatedId || $[1] !== id) {
    t1 = isNonEmptyString(id) ? id : generatedId;
    $[0] = generatedId;
    $[1] = id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const checkboxId = t1;
  let t2;
  if ($[3] !== description) {
    t2 = isNonEmptyString(description);
    $[3] = description;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hasDescription = t2;
  let t3;
  if ($[5] !== error) {
    t3 = isNonEmptyString(error);
    $[5] = error;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const hasError = t3;
  const descriptionId = hasDescription ? `${checkboxId}-description` : undefined;
  const errorId = hasError ? `${checkboxId}-error` : undefined;
  let t4;
  if ($[7] !== descriptionId || $[8] !== errorId) {
    t4 = [descriptionId, errorId].filter(isNonEmptyString);
    $[7] = descriptionId;
    $[8] = errorId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const describedByIds = t4;
  const ariaDescribedBy = describedByIds.length > 0 ? describedByIds.join(" ") : undefined;
  let t5;
  if ($[10] !== containerClassName) {
    t5 = cn("flex flex-col gap-1.5", containerClassName);
    $[10] = containerClassName;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = disabled === true ? "cursor-not-allowed opacity-50" : "cursor-pointer";
  let t7;
  if ($[12] !== labelClassName || $[13] !== t6) {
    t7 = cn("flex items-start gap-2 leading-5", t6, labelClassName);
    $[12] = labelClassName;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== name) {
    t8 = name !== undefined ? {
      name
    } : {};
    $[15] = name;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== onCheckedChange) {
    t9 = next => {
      onCheckedChange(next === true);
    };
    $[17] = onCheckedChange;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== disabled) {
    t10 = disabled !== undefined ? {
      disabled
    } : {};
    $[19] = disabled;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== required) {
    t11 = required !== undefined ? {
      required
    } : {};
    $[21] = required;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== ariaDescribedBy) {
    t12 = ariaDescribedBy !== undefined ? {
      "aria-describedby": ariaDescribedBy
    } : {};
    $[23] = ariaDescribedBy;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== hasError) {
    t13 = hasError ? {
      "aria-invalid": true as const
    } : {};
    $[25] = hasError;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== autoComplete) {
    t14 = autoComplete !== undefined ? {
      autoComplete
    } : {};
    $[27] = autoComplete;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = disabled === true ? "cursor-not-allowed" : "cursor-pointer";
  let t16;
  if ($[29] !== t15) {
    t16 = cn("mt-0.5 rounded-item", t15);
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== checkboxId || $[32] !== checked || $[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t13 || $[37] !== t14 || $[38] !== t16 || $[39] !== t8 || $[40] !== t9) {
    t17 = <Checkbox id={checkboxId} {...t8} checked={checked} onCheckedChange={t9} {...t10} {...t11} {...t12} {...t13} {...t14} className={t16} />;
    $[31] = checkboxId;
    $[32] = checked;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t16;
    $[39] = t8;
    $[40] = t9;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== required) {
    t18 = required === true && <span aria-hidden={true}> *</span>;
    $[42] = required;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== label || $[45] !== t18) {
    t19 = <span>{label}{t18}</span>;
    $[44] = label;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== checkboxId || $[48] !== t17 || $[49] !== t19 || $[50] !== t7) {
    t20 = <Label htmlFor={checkboxId} className={t7}>{t17}{t19}</Label>;
    $[47] = checkboxId;
    $[48] = t17;
    $[49] = t19;
    $[50] = t7;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== description || $[53] !== descriptionClassName || $[54] !== descriptionId || $[55] !== hasDescription) {
    t21 = hasDescription && <p id={descriptionId} className={cn("pl-6 text-caption text-foreground-secondary", descriptionClassName)}>{description}</p>;
    $[52] = description;
    $[53] = descriptionClassName;
    $[54] = descriptionId;
    $[55] = hasDescription;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== error || $[58] !== errorClassName || $[59] !== errorId || $[60] !== hasError) {
    t22 = hasError && <p id={errorId} role="alert" className={cn("pl-6 text-caption text-feedback-error-content", errorClassName)}>{error}</p>;
    $[57] = error;
    $[58] = errorClassName;
    $[59] = errorId;
    $[60] = hasError;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== t20 || $[63] !== t21 || $[64] !== t22 || $[65] !== t5) {
    t23 = <div className={t5}>{t20}{t21}{t22}</div>;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
    $[65] = t5;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  return t23;
}